<script setup lang="ts">
/**
 * TheAdminBar — slanke donkere beheerbalk bóven de header.
 *
 * Alleen zichtbaar wanneer bewerken is toegestaan (ingelogd, geen
 * voorbeeldweergave); in voorbeeldweergave staat hier de amber balk
 * met "Terug naar beheer" (App.vue). Bevat de beheer-chrome die
 * eerder in de header zelf zat: voorbeeldknop, instellingen en het
 * gebruikersmenu — zodat de header puur navigatie voor bezoekers is.
 */

import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../stores/auth'
import { useSectionStore } from '../../stores/sections'
import { useUiStore } from '../../stores/ui'
import { useAccessStore } from '../../stores/access'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { User, LogOut, Settings, Eye, Lock, Globe, Users, Layers, Check, ChevronDown, CircleHelp, ExternalLink, Rocket } from 'lucide-vue-next'
import { computed, onMounted } from 'vue'
import { useHubStore } from '../../stores/hubs'
import { useOnboardingStore } from '../../stores/onboarding'
import { helpUrl as resolveHelpUrl } from '../../utils/helpUrl'

const authStore = useAuthStore()
const ui = useUiStore()
const access = useAccessStore()
const hubs = useHubStore()
const { t } = useI18n()

const sections = useSectionStore()
const onboarding = useOnboardingStore()
const route = useRoute()
const router = useRouter()

/*
 * Het helpcenter staat op een eigen adres (een Pentrical-omgeving als elke
 * andere), dus het is een gewone externe link en geen route. Zonder
 * VITE_HELP_URL valt de helper terug op het publieke helpcenter, zodat het
 * item er altijd staat.
 */
const helpUrl = computed(() => resolveHelpUrl())

/**
 * Voorbeeldweergave starten - en meteen naar het publieke adres.
 *
 * Sta je op /pages/3, dan blijft dat adres zonder deze stap in de balk
 * staan: je ziet dan wél het bezoekersbeeld, maar kunt de link niet delen,
 * want /pages/3 stuurt een uitgelogde bezoeker naar het inlogscherm.
 *
 * Heeft de pagina geen publiek adres (concept of verborgen), dan blijven we
 * staan waar we zijn: er valt niets te tonen, en doorsturen naar een adres
 * dat 404't is erger dan blijven.
 */
function bekijkAlsBezoeker() {
  const id = route.name === 'page' ? Number(route.params.id) : null
  const publiek = id !== null && !Number.isNaN(id) ? sections.publicPathForPage(id) : null

  if (publiek && publiek !== route.path) {
    ui.startPreview(route.fullPath)
    router.push(publiek)
    return
  }

  ui.startPreview()
}

/* De wisselaar verschijnt pas bij een tweede hub: met één documentatie-
   site is een keuzemenu alleen maar ruis. De onboarding-voortgang voedt
   de Aan-de-slag-chip; de store bewaakt zelf dat er maar één verzoek
   uitgaat. */
onMounted(() => {
  hubs.fetchHubs().catch(() => {})
  if (authStore.isEditor) onboarding.fetchSteps()
})

/* Of de site openstaat mag je nooit hoeven gokken: die stand staat hier
   altijd, met één klik naar de instelling. */
const accessIcon = computed(() =>
  access.mode === 'team' ? Users : access.mode === 'password' ? Lock : Globe
)

const accessLabel = computed(() => {
  switch (access.mode) {
    case 'password': return t('access.modes.password')
    case 'team': return t('access.modes.team')
    default: return t('access.modes.public')
  }
})
</script>

<template>
  <div
    class="flex items-center justify-end gap-1 h-10 px-4 sm:px-6 bg-gray-900 text-gray-200 text-sm"
    role="region"
    :aria-label="t('chrome.adminBar.label')"
  >
    <div class="mr-auto flex items-center gap-1">
      <!-- Toegangsstand van de site (openbaar / wachtwoord / alleen team) -->
      <RouterLink
        to="/settings/access"
        class="flex items-center gap-2 px-2 py-1 rounded-md text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
        :title="t('access.adminBar.title', { mode: accessLabel })"
      >
        <component :is="accessIcon" class="w-3.5 h-3.5" aria-hidden="true" />
        <span class="hidden sm:block text-xs">{{ accessLabel }}</span>
      </RouterLink>

      <!-- Voortgang van de startstappen; verdwijnt zodra alles af is.
           Het scherm zelf blijft bereikbaar via het gebruikersmenu. -->
      <RouterLink
        v-if="authStore.isEditor && onboarding.loaded && !onboarding.allDone"
        to="/start"
        class="flex items-center gap-2 px-2 py-1 rounded-md text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
        :title="t('home.onboarding.progress', { done: onboarding.doneCount, total: onboarding.total })"
      >
        <Rocket class="w-3.5 h-3.5" aria-hidden="true" />
        <span class="hidden sm:block text-xs">{{ t('chrome.adminBar.gettingStarted') }}</span>
        <span class="text-xs font-semibold rounded-full bg-white/10 px-1.5">{{ onboarding.doneCount }}/{{ onboarding.total }}</span>
      </RouterLink>
    </div>

    <!-- Hubwisselaar: welke documentatiesite je nu bewerkt (§14) -->
    <Menu v-if="hubs.heeftMeerdereHubs" as="div" class="relative">
      <MenuButton
        class="flex items-center gap-2 px-2 py-1 rounded-md text-sm font-medium hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
        :aria-label="t('account.hubs.switcherLabel')"
      >
        <Layers class="w-4 h-4" aria-hidden="true" />
        <span class="hidden sm:block max-w-[10rem] truncate">{{ hubs.actief?.name }}</span>
        <ChevronDown class="w-3 h-3" aria-hidden="true" />
      </MenuButton>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <MenuItems
          class="absolute left-0 mt-2 w-56 origin-top-left bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-1"
        >
          <!--
            Alleen de lijst scrollt, niet het hele menu. Zonder deze wrapper
            groeit het menu mee met het aantal hubs (elke regel ~36px) en loopt
            het bij een stuk of vijfentwintig van het scherm af — inclusief de
            "hubs beheren"-link hieronder, juist het scherm waar je er dan een
            zou opruimen. Het plan begrenst Team op 10 hubs, dus bij de normale
            maximumstand is er geen schuifbalk; dit vangt af wat daarbovenop
            komt via quota_overrides, want dat is per account op te hogen.
          -->
          <div class="max-h-96 overflow-y-auto overscroll-contain">
            <MenuItem v-for="hub in hubs.zichtbaar" :key="hub.id" v-slot="{ active }">
              <button
                type="button"
                class="flex w-full items-center gap-2 px-3 py-2 text-sm text-left"
                :class="active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'"
                @click="hubs.switchTo(hub.id)"
              >
                <Check
                  class="w-4 h-4 flex-shrink-0"
                  :class="hub.id === hubs.currentHubId ? 'text-primary-600' : 'text-transparent'"
                  aria-hidden="true"
                />
                <span class="truncate">{{ hub.name }}</span>
              </button>
            </MenuItem>
          </div>

          <div class="my-1 border-t border-gray-100"></div>

          <MenuItem v-slot="{ active }">
            <RouterLink
              to="/account/hubs"
              class="flex items-center gap-2 px-3 py-2 text-sm"
              :class="active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'"
            >
              <Layers class="w-4 h-4" aria-hidden="true" />
              {{ t('account.hubs.manage') }}
            </RouterLink>
          </MenuItem>
        </MenuItems>
      </transition>
    </Menu>

    <!-- Voorbeeldweergave starten -->
    <button
      type="button"
      class="flex items-center gap-2 px-3 py-1 rounded-md hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
      :title="t('chrome.adminBar.viewAsVisitorTitle')"
      @click="bekijkAlsBezoeker()"
    >
      <Eye class="w-4 h-4" aria-hidden="true" />
      {{ t('chrome.adminBar.viewAsVisitor') }}
    </button>

    <!-- Instellingen. Met het woord erbij: een kaal tandwiel liet te veel
         aan de gebruiker over om te raden. Expliciete tekstkleur nodig:
         dit is een <a>, en de globale a-stijl (main.css) zou anders de
         donkerblauwe linkkleur laten zien in plaats van de balktekst. -->
    <RouterLink
      to="/settings"
      class="flex items-center gap-2 px-3 py-1 rounded-md text-gray-200 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
    >
      <Settings class="w-4 h-4" aria-hidden="true" />
      {{ t('chrome.adminBar.settings') }}
    </RouterLink>

    <!-- Gebruikersmenu -->
    <Menu as="div" class="relative">
      <MenuButton
        class="flex items-center gap-2 px-2 py-1 rounded-md text-sm font-medium hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
      >
        <span class="sr-only">{{ t('chrome.adminBar.openUserMenu') }}</span>
        <div class="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
          <User class="w-4 h-4 text-primary-600" aria-hidden="true" />
        </div>
        <span class="hidden sm:block">{{ authStore.user?.name }}</span>
      </MenuButton>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <MenuItems
          class="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
        >
          <!-- Naar het account-scherm (profiel, team en — voor owners —
               facturering); site-instellingen hebben hun eigen
               tandwiel-knop naast dit menu. -->
          <div class="py-1">
            <MenuItem v-slot="{ active }">
              <RouterLink
                to="/account/profiel"
                :class="[
                  active ? 'bg-gray-100' : '',
                  'flex items-center gap-2 px-4 py-2 text-sm text-gray-700'
                ]"
              >
                <User class="w-4 h-4" aria-hidden="true" />
                {{ t('chrome.adminBar.myAccount') }}
              </RouterLink>
            </MenuItem>
          </div>
          <!-- Helpcenter: eigen omgeving op een eigen adres, dus een gewone
               externe link. In een nieuw tabblad, zodat je je werk hier niet
               kwijtraakt om iets op te zoeken. Aan de slag staat erboven:
               dit menu is de vaste plek waar het scherm bereikbaar blijft
               als de voortgangschip weg is. -->
          <div v-if="helpUrl" class="py-1 border-t border-gray-100">
            <MenuItem v-if="authStore.isEditor" v-slot="{ active }">
              <RouterLink
                to="/start"
                :class="[
                  active ? 'bg-gray-100' : '',
                  'flex items-center gap-2 px-4 py-2 text-sm text-gray-700'
                ]"
              >
                <Rocket class="w-4 h-4" aria-hidden="true" />
                {{ t('chrome.adminBar.gettingStarted') }}
              </RouterLink>
            </MenuItem>
            <MenuItem v-slot="{ active }">
              <a
                :href="helpUrl"
                target="_blank"
                rel="noopener noreferrer"
                :class="[
                  active ? 'bg-gray-100' : '',
                  'flex items-center gap-2 px-4 py-2 text-sm text-gray-700'
                ]"
              >
                <CircleHelp class="w-4 h-4" aria-hidden="true" />
                {{ t('chrome.adminBar.help') }}
                <ExternalLink class="w-3 h-3 ml-auto text-gray-400" aria-hidden="true" />
              </a>
            </MenuItem>
          </div>
          <div class="py-1 border-t border-gray-100">
            <MenuItem v-slot="{ active }">
              <button
                type="button"
                :class="[
                  active ? 'bg-gray-100' : '',
                  'flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700'
                ]"
                @click="authStore.logout()"
              >
                <LogOut class="w-4 h-4" aria-hidden="true" />
                {{ t('chrome.adminBar.logout') }}
              </button>
            </MenuItem>
          </div>
        </MenuItems>
      </transition>
    </Menu>
  </div>
</template>

<style scoped>
/* Links in de balk erven anders de globale a-underline uit main.css. */
div :deep(a) {
  text-decoration: none;
}
</style>
